/**
 * A visible countdown, in seconds.
 *
 * `auth-reset.html`'s note: "Resend is disabled on a visible countdown rather
 * than silently rate-limited." That is the whole reason this exists — a button
 * that silently does nothing reads as broken, and the person clicks it again.
 *
 * Story 3.4.
 */
import { useCallback, useEffect, useState } from 'react'

export function useCountdown(): { remaining: number; start: (seconds: number) => void } {
  const [until, setUntil] = useState(0)
  const [remaining, setRemaining] = useState(0)

  useEffect(() => {
    if (until === 0) return
    const tick = () => setRemaining(Math.max(0, Math.ceil((until - Date.now()) / 1000)))
    tick()
    const timer = setInterval(tick, 1000)
    return () => clearInterval(timer)
  }, [until])

  // Stable across renders, so a caller can list it as an effect dependency
  // without the effect re-firing on every keystroke elsewhere on the screen.
  const start = useCallback((seconds: number) => setUntil(Date.now() + seconds * 1000), [])

  return { remaining, start }
}
